import { useCallback } from 'react';
import { useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { HttpStatusCode, type AxiosError } from 'axios';
import AssetManagerService, { getAssetManagerErrorMessage, type AssetFieldValuesParams } from '../services/AssetManager/assetManager.service';
import { assetManagerServiceMessages } from '../services/AssetManager/assetManager.constants';
import Notification from '../shared/components/Notification';
import { NotificationTypes } from '../enums/notificationTypes';
import { QueryKeys } from '../enums/queryKeys';
import type { AssetLayerField, AssetManagerLayer, AssetStatistic, AssetStatisticInput, AssetStatistics } from '../types/InfrastructureManagerTypes';

const MESSAGES = assetManagerServiceMessages.STATISTICS;
const STATISTICS_KEY = [QueryKeys.GET_ASSET_STATISTICS];
const NO_STATISTICS: AssetStatistic[] = [];
const NO_LAYERS: AssetManagerLayer[] = [];
const NO_FIELDS: AssetLayerField[] = [];

// A 409 (limit reached) is correctable, so it warns rather than errors.
const notifyStatisticError = (message: string, error: unknown) =>
  Notification({
    message,
    description: getAssetManagerErrorMessage(error),
    type: (error as AxiosError)?.response?.status === HttpStatusCode.Conflict ? NotificationTypes.WARNING : NotificationTypes.ERROR,
  });

const notifySuccess = (message: string) => Notification({ message, type: NotificationTypes.SUCCESS });

const updateStatisticsCache = (queryClient: QueryClient, update: (statistics: AssetStatistic[]) => AssetStatistic[]) =>
  queryClient.setQueryData<AssetStatistics>(STATISTICS_KEY, (current) => current && { ...current, statistics: update(current.statistics) });

export const useAssetStatistics = () => {
  const queryClient = useQueryClient();
  const { getStatistics, deleteStatistic } = AssetManagerService();
  const { data, error: statisticsError, isLoading: isLoadingStatistics, isLoadingError: isStatisticsLoadingError } = useQuery(getStatistics());

  // Removed from the row at once (story S7) and restored if the request fails.
  const { mutate: removeStatistic } = useMutation({
    ...deleteStatistic(),
    onMutate: async (id) => {
      await queryClient.cancelQueries({ queryKey: STATISTICS_KEY });
      const previous = queryClient.getQueryData<AssetStatistics>(STATISTICS_KEY);
      updateStatisticsCache(queryClient, (statistics) => statistics.filter((statistic) => statistic.id !== id));
      return { previous };
    },
    onSuccess: () => notifySuccess(MESSAGES.DELETE_SUCCESS),
    onError: (error, _id, context) => {
      if (context?.previous) queryClient.setQueryData(STATISTICS_KEY, context.previous);
      notifyStatisticError(MESSAGES.DELETE_ERROR, error);
    },
  });

  return {
    statistics: data?.statistics ?? NO_STATISTICS,
    maxStatistics: data?.maxStatistics,
    statisticsError,
    isLoadingStatistics,
    isStatisticsLoadingError,
    removeStatistic,
  };
};

// Writes the saved widget from the response, so it shows (with its computed value) without a refetch.
export const useSaveAssetStatistic = () => {
  const queryClient = useQueryClient();
  const { createStatistic, updateStatistic } = AssetManagerService();

  const { mutateAsync: createAsync, isPending: isCreating } = useMutation({
    ...createStatistic(),
    onSuccess: (created) => {
      updateStatisticsCache(queryClient, (statistics) => [...statistics, created]);
      notifySuccess(MESSAGES.CREATE_SUCCESS);
    },
    onError: (error) => notifyStatisticError(MESSAGES.CREATE_ERROR, error),
  });

  const { mutateAsync: updateAsync, isPending: isUpdating } = useMutation({
    ...updateStatistic(),
    onSuccess: (updated) => {
      updateStatisticsCache(queryClient, (statistics) => statistics.map((statistic) => (statistic.id === updated.id ? updated : statistic)));
      notifySuccess(MESSAGES.UPDATE_SUCCESS);
    },
    onError: (error) => notifyStatisticError(MESSAGES.UPDATE_ERROR, error),
  });

  const saveStatistic = useCallback(
    (input: AssetStatisticInput, id?: number) => (id === undefined ? createAsync(input) : updateAsync({ id, input })),
    [createAsync, updateAsync],
  );

  return { saveStatistic, isSavingStatistic: isCreating || isUpdating };
};

// Editor-only data: mounted with the setup popover, so nothing is fetched or held while it is closed.
// isLoading, not isFetching: true only before a key has data, so refetches keep the cached options.
export const useStatisticEditorOptions = (layerId: number | null) => {
  const { getMetaLayers, getLayerFields } = AssetManagerService();
  const { data: layers = NO_LAYERS, isLoading: isLoadingLayers, error: layersError } = useQuery(getMetaLayers());
  const {
    data,
    isLoading: isLoadingFields,
    error: fieldsError,
  } = useQuery({ ...getLayerFields(layerId ?? 0), enabled: layerId !== null });
  const fields = data?.fields ?? NO_FIELDS;

  return { layers, isLoadingLayers, layersError, fields, isLoadingFields, fieldsError };
};

// Only enabled while a WHERE value dropdown is open.
export const useStatisticFieldValues = (params: AssetFieldValuesParams, enabled: boolean) => {
  const { getFieldValues } = AssetManagerService();
  const { data, isLoading: isLoadingValues, error: valuesError } = useQuery({ ...getFieldValues(params), enabled });
  return { values: data?.values, isLoadingValues, valuesError };
};
